import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DeFiPositions = ({ positions, opportunities, onStake, onUnstake }) => {
  const [activeTab, setActiveTab] = useState('positions');

  const totalStaked = positions?.stakingPositions?.reduce(
    (sum, pos) => sum + pos?.currentValue, 0
  ) || 0;

  const totalEarned = positions?.totalEarned || 0;

  return (
    <div className="card">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-foreground mb-1">DeFi Positions</h3>
        <p className="text-sm text-muted-foreground">Staking and yield farming</p>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <div className="p-4 bg-primary/10 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Lock" size={16} className="text-primary" />
            <p className="text-xs text-muted-foreground">Total Staked</p>
          </div>
          <p className="text-xl font-bold text-foreground">
            ${totalStaked?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
        </div>

        <div className="p-4 bg-success/10 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingUp" size={16} className="text-success" />
            <p className="text-xs text-muted-foreground">Total Earned</p>
          </div>
          <p className="text-xl font-bold text-success">
            ${totalEarned?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
        </div>

        <div className="p-4 bg-accent/10 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Activity" size={16} className="text-accent" />
            <p className="text-xs text-muted-foreground">Active Positions</p>
          </div>
          <p className="text-xl font-bold text-foreground">
            {positions?.activePositions || 0}
          </p>
        </div>
      </div>
      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {['positions', 'opportunities']?.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-smooth capitalize ${
              activeTab === tab
                ? 'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>
      {/* Active Positions */}
      {activeTab === 'positions' && (
        <div className="space-y-3">
          {positions?.stakingPositions?.length > 0 ? (
            positions?.stakingPositions?.map(position => (
              <div key={position?.id} className="p-4 bg-muted/30 rounded-lg">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-1">
                      {position?.protocol}
                    </p>
                    <p className="text-xs text-muted-foreground">{position?.asset} Staking</p>
                  </div>
                  <div className="px-2 py-1 bg-success/20 text-success text-xs rounded">
                    {position?.apy}% APY
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Staked Amount</p>
                    <p className="text-sm font-semibold text-foreground">
                      {position?.stakedAmount} {position?.asset}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      ${position?.currentValue?.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Earned</p>
                    <p className="text-sm font-semibold text-success">
                      {position?.earned} {position?.asset}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      ${position?.earnedValue?.toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Plus"
                    onClick={() => onStake?.(position)}
                    className="flex-1"
                  >
                    Add More
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Minus"
                    onClick={() => onUnstake?.(position)}
                    className="flex-1"
                  >
                    Unstake
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <Icon name="Inbox" size={48} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground mb-4">No active positions</p>
              <Button
                variant="primary"
                size="sm"
                iconName="Plus"
                onClick={() => setActiveTab('opportunities')}
              >
                Explore Opportunities
              </Button>
            </div>
          )}
        </div>
      )}
      {/* Staking Opportunities */}
      {activeTab === 'opportunities' && (
        <div className="space-y-3">
          {opportunities?.map(opp => (
            <div key={opp?.id} className="p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-smooth">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${opp?.color}15` }}
                  >
                    <Icon name={opp?.icon} size={20} color={opp?.color} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{opp?.protocol}</p>
                    <p className="text-xs text-muted-foreground">{opp?.asset} Staking</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-success">{opp?.apy}%</p>
                  <p className="text-xs text-muted-foreground">APY</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
                <div>
                  <p className="text-muted-foreground mb-1">TVL</p>
                  <p className="font-medium text-foreground">
                    ${(opp?.tvl / 1e9)?.toFixed(2)}B
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1">Min Stake</p>
                  <p className="font-medium text-foreground">
                    {opp?.minStake} {opp?.asset}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1">Lock Period</p>
                  <p className="font-medium text-foreground">{opp?.lockPeriod}</p>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`px-2 py-1 rounded text-xs ${
                    opp?.risk === 'Low' ? 'bg-success/20 text-success' :
                    opp?.risk === 'Medium'? 'bg-warning/20 text-warning' : 'bg-error/20 text-error'
                  }`}>
                    {opp?.risk} Risk
                  </div>
                  <div className="px-2 py-1 bg-muted rounded text-xs text-muted-foreground">
                    {opp?.chain}
                  </div>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  iconName="Lock"
                  onClick={() => onStake?.(opp)}
                >
                  Stake
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DeFiPositions;
